import React, { useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { get, uniqBy } from 'lodash-es';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildEvTestedDataPath,
  FeatureSpecsEntities,
  FeatureSpecsPaths,
  VehicleEntities,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import {
  buildTrimSummariesPath,
  InventoryCoreConfigurationsModel,
  InventoryTrimSummaryEntities,
} from 'client/data/models/inventory-core-configurations';
import { PageModel } from 'client/data/models/page';
import { getTrimsWithLabels } from 'site-modules/shared/utils/core-page/trims-cta-utils';
import { CorePageParams, getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getFeaturesSpecsUrlStandard, transformMmsyAndParams } from 'site-modules/shared/utils/core-link-constructor';
import { SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { EV_TESTED_DATA_START_DATE } from 'site-modules/shared/constants/range-and-cost';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import { Link } from 'site-modules/shared/components/link/link';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { SpeculationRule } from 'site-modules/shared/components/speculation-rule/speculation-rule';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';
import { SubmodelTrimSelect, ANCHOR_ID } from './submodel-trim-select';
import { FeaturesList } from './features-list';

import './features-highlights.scss';

function FeaturesHighlightsUI({
  className = null,
  stylesFeatures = null,
  trims = null,
  sharedTrimName = null,
  setModelValue,
  stylesEvTestedData = null,
  params,
  vehicle,
  isMobile = false,
}) {
  const [styleId, setStyleId] = useState(null);
  const prevSharedTrimName = usePrevious(sharedTrimName);

  if (prevSharedTrimName !== sharedTrimName) {
    setStyleId(null);
  }
  const containerRef = useRef();

  const onTrimChange = useCallback(
    ({ trimName }) => {
      setModelValue('sharedValues.trimName', PageModel, trimName);
    },
    [setModelValue]
  );

  const onStyleChange = useCallback(({ id }) => {
    setStyleId(id);
  }, []);

  if ((!trims && !params.isUsed) || !get(stylesFeatures, 'length')) {
    return null;
  }

  const {
    make: { slug: makeSlug, name: makeName },
    model: { slug: modelSlug, name: modelName },
    submodels: { slug: submodelSlug },
    year,
  } = vehicle;

  const selectedSubmodelId = `${year}|${submodelSlug}`;
  // build trims from stylesFeatures for used vehicles
  const currentTrims =
    trims ||
    uniqBy(stylesFeatures, 'trim').map(trim => ({
      trimName: trim.trim,
      styleId: trim.id,
    }));

  if (!currentTrims?.length) {
    return null;
  }

  const currentTrim =
    (currentTrims && currentTrims.find(({ trimName }) => trimName === sharedTrimName)) || get(currentTrims, '[0]');
  const currentStyleId = styleId || currentTrim.styleId;
  const styleFeatures = stylesFeatures.find(({ id }) => id === currentStyleId);
  const styleEvTestedData =
    stylesEvTestedData && stylesEvTestedData.find(({ styleId: evStyleId }) => +evStyleId === currentStyleId);

  const isElectric = !!styleFeatures?.styleAttributes?.electric;
  const isPHEV = !!styleFeatures?.styleAttributes?.pluginElectric;

  const featuresSpecsUrl = getFeaturesSpecsUrlStandard(
    transformMmsyAndParams({ makeModelSubmodelYear: vehicle, params }),
    currentStyleId
  );

  return (
    <ScrollElement
      id={MENU_HASH.FEATURES_SPECS}
      innerRef={containerRef}
      className={classnames('card pos-r d-block features-highlights px-1 pt-1 pb-1_5', className)}
      data-tracking-parent="features-and-specs"
    >
      <ScrollElement id={ANCHOR_ID} style={{ position: 'absolute', top: `${SCROLL_OFFSET}px` }} />
      <h2 className="heading-3 mb-0_5">
        {params.isUsed ? `${year} ${modelName} Features & Specs` : `${year} ${makeName} ${modelName} Features & Specs`}
      </h2>
      <SubmodelTrimSelect
        params={params}
        className="mb-1"
        selectedSubmodelId={selectedSubmodelId}
        currentTrim={currentTrim}
        trims={currentTrims}
        onTrimChange={onTrimChange}
      />
      <hr className="mt-0 mb-1" />
      <FeaturesList
        vehicleParams={{ makeSlug, modelSlug, makeName, modelName, year }}
        stylesFeatures={stylesFeatures}
        styleId={currentStyleId}
        styleEvTestedData={styleEvTestedData}
        onStyleChange={onStyleChange}
        isMobile={isMobile}
        isUsed={params.isUsed || params.isMycoNewCore}
      />
      <hr className="mt-1 mb-1_5" />
      {!params.isUsed && (isElectric || isPHEV) ? (
        <div className="d-flex flex-column flex-md-row">
          <Button
            tag={ScrollLink}
            to={MENU_HASH.EV_INSIGHTS}
            scrollConfig={{ offset: SCROLL_OFFSET }}
            size="sm"
            color="outline-blue-50"
            className={classnames('size-16 text-transform-none fw-medium', {
              'w-100 mb-1': isMobile,
              'me-1': !isMobile,
            })}
            data-tracking-id="view_EV_insights"
          >
            See Edmunds {isPHEV ? 'PHEV' : 'EV'} insights
          </Button>
          <Button
            tag={Link}
            to={featuresSpecsUrl}
            size="sm"
            color="outline-blue-50"
            className={classnames('size-16 text-transform-none fw-medium', { 'w-100': isMobile })}
            data-tracking-id="view_features_specs"
          >
            See all features & specs
          </Button>
        </div>
      ) : (
        <div className="text-center">
          <Button
            tag={Link}
            to={featuresSpecsUrl}
            size="sm"
            color="outline-primary-b"
            className={classnames('size-16 text-transform-none', { 'w-100': isMobile })}
            data-tracking-id="view_features_specs"
          >
            See all features & specs
          </Button>
        </div>
      )}

      {!params.isUsed && <SpeculationRule observeRef={containerRef} urls={[featuresSpecsUrl]} />}
    </ScrollElement>
  );
}

FeaturesHighlightsUI.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  setModelValue: PropTypes.func.isRequired,
  className: PropTypes.string,
  stylesFeatures: PropTypes.arrayOf(FeatureSpecsEntities.FeatureStyleEntity),
  trims: InventoryTrimSummaryEntities.TrimSummaries,
  sharedTrimName: PropTypes.string,
  stylesEvTestedData: PropTypes.arrayOf(VehicleEntities.StyleEvTestedData),
  isMobile: PropTypes.bool,
};

const FeaturesHighlightsWrapper = connectToModel(FeaturesHighlightsUI, {
  stylesEvTestedData: bindToPath(
    ({ stylesFeatures, vehicle }) =>
      !!stylesFeatures &&
      Object.keys(get(vehicle, 'edmundsTypeCategories', {})).includes('electric') &&
      vehicle.year >= EV_TESTED_DATA_START_DATE &&
      buildEvTestedDataPath(stylesFeatures.map(({ id }) => id)),
    VehicleModel
  ),
});

export const FeaturesHighlights = connectToModel(FeaturesHighlightsWrapper, {
  sharedTrimName: bindToPath('sharedValues.trimName', PageModel),
  stylesFeatures: bindToPath(
    ({ vehicle }) => FeatureSpecsPaths.buildPartialFeaturesPath(getParamsFromVehicle(vehicle)),
    VehicleModel
  ),
  trims: bindToPath(
    ({ vehicle, params }) =>
      (!params.isUsed || params.isMycoNewCore) && buildTrimSummariesPath(getParamsFromVehicle(vehicle)),
    InventoryCoreConfigurationsModel,
    getTrimsWithLabels
  ),
});
